import { MouseEvent, useState } from 'react';

import cn from 'classnames';

import { formatMessageDate } from '@entities/message/lib';
import { ICommentMessage, IMessageAttachment } from '@entities/message/model/types';
import { Flex, Icon, UiButton, UiText } from '@shared/ui';

import { MessageAttachments } from '../MessageAttachments';
import cls from './MessageItem.module.css';

interface IMessageItemProps {
    className?: string;
    messageData: ICommentMessage;
    onDeleteMessage?: (messageId: ICommentMessage['id']) => void;
    onOpenAttachments?: (
        attachments: IMessageAttachment[],
        currentAttachmentId: IMessageAttachment['id']
    ) => void;
}

export const MessageItem = ({
    className,
    messageData,
    onDeleteMessage,
    onOpenAttachments,
}: IMessageItemProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const messageTime = formatMessageDate(messageData?.datetime);
    const canDeleteMessage = Boolean(onDeleteMessage) && messageData.canDelete;

    const handleMouseEnter = () => {
        if (!canDeleteMessage) return;
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        if (!canDeleteMessage) return;
        setIsHovered(false);
    };

    const handleDeleteMessage = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onDeleteMessage?.(messageData.id);
    };

    return (
        <Flex
            gap={4}
            className={cn(cls.MessageItemContainer)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            fullWidth
        >
            <Flex
                as="li"
                direction="column"
                className={cn(cls.MessageItem, messageData.isMine && cls.isYours, className)}
            >
                {canDeleteMessage && isHovered && (
                    <UiButton
                        className={cls.DeleteMessageBtn}
                        variant="icon"
                        startIcon={<Icon iconName="taskDelete" />}
                        onClick={handleDeleteMessage}
                    />
                )}

                <MessageAttachments
                    imgAttachments={messageData?.images}
                    otherAttachments={messageData?.files}
                    onOpenAttachments={onOpenAttachments}
                />
                <Flex fullWidth className={cls.MessageItemInfo} direction="column" gap={12}>
                    <Flex fullWidth>
                        <UiText tag="span" variant="mediumPlain" className={cls.MessageText}>
                            {messageData?.text}
                        </UiText>
                    </Flex>
                    <Flex gap={8} justify="end" fullWidth>
                        {messageData.user && (
                            <UiText tag="span" variant="mediumPlain" className={cls.MessageMeta}>
                                {messageData?.user?.name}
                            </UiText>
                        )}
                        <UiText tag="span" variant="mediumPlain" className={cls.MessageMeta}>
                            {messageTime}
                        </UiText>
                    </Flex>
                </Flex>
            </Flex>
        </Flex>
    );
};
